"use strict";

export const BLOCKED_STORAGE_KEYS = Object.freeze([
  "askwoundbot_history",
  "askwoundbot_session",
]);

const BLOCKED_STORAGE_KEY_SET = new Set(BLOCKED_STORAGE_KEYS);
const BOUNDARY_MARKER = Symbol.for("woundos.browserPrivacy.windowStorage.v3");
const STORAGE_NAMES = Object.freeze(["localStorage", "sessionStorage"]);

function storageKey(value) {
  return typeof value === "symbol" ? null : String(value ?? "");
}

function isBlockedProperty(value) {
  const key = storageKey(value);
  return key !== null && BLOCKED_STORAGE_KEY_SET.has(key);
}

function safeStorage(windowObject, name) {
  try {
    return windowObject?.[name] ?? null;
  } catch {
    return null;
  }
}

function storageMethods(storage) {
  const getItem = storage.getItem.bind(storage);
  const setItem = storage.setItem.bind(storage);
  const removeItem = storage.removeItem.bind(storage);
  return { getItem, setItem, removeItem };
}

function createStorageProxy(storage) {
  const methods = storageMethods(storage);
  return new Proxy(storage, {
    get(target, property) {
      if (isBlockedProperty(property)) return null;
      if (property === "getItem") {
        return (key) => (isBlockedProperty(key) ? null : methods.getItem(key));
      }
      if (property === "setItem") {
        return (key, value) => {
          if (isBlockedProperty(key)) {
            methods.removeItem(String(key));
            return undefined;
          }
          return methods.setItem(key, value);
        };
      }
      if (property === "removeItem") {
        return (key) => methods.removeItem(key);
      }
      const value = Reflect.get(target, property, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
    set(_target, property, value) {
      if (isBlockedProperty(property)) {
        methods.removeItem(String(property));
        return true;
      }
      if (typeof property === "string") {
        methods.setItem(property, value);
        return true;
      }
      return false;
    },
    defineProperty(_target, property, descriptor) {
      if (isBlockedProperty(property)) {
        methods.removeItem(String(property));
        return true;
      }
      if (typeof property === "string" && "value" in descriptor) {
        methods.setItem(property, descriptor.value);
        return true;
      }
      return false;
    },
    deleteProperty(_target, property) {
      if (typeof property === "string") {
        methods.removeItem(property);
        return true;
      }
      return false;
    },
    has(target, property) {
      if (isBlockedProperty(property)) return false;
      return Reflect.has(target, property);
    },
    ownKeys(target) {
      return Reflect.ownKeys(target).filter((property) => !isBlockedProperty(property));
    },
    getOwnPropertyDescriptor(target, property) {
      if (isBlockedProperty(property)) return undefined;
      return Reflect.getOwnPropertyDescriptor(target, property);
    },
  });
}

export function purgeLegacyBrowserState(windowObject = globalThis.window) {
  let removed = 0;
  for (const storageName of STORAGE_NAMES) {
    const storage = safeStorage(windowObject, storageName);
    if (!storage) continue;
    for (const key of BLOCKED_STORAGE_KEYS) {
      try {
        if (storage.getItem(key) !== null) removed += 1;
        storage.removeItem(key);
        Reflect.deleteProperty(storage, key);
      } catch {
        // An unavailable storage surface is already fail-closed.
      }
    }
  }
  return removed;
}

export function installStorageBoundary(windowObject = globalThis.window) {
  if (!windowObject) throw new Error("browser_storage_boundary_unavailable");
  if (windowObject[BOUNDARY_MARKER]) return false;

  for (const storageName of STORAGE_NAMES) {
    const storage = safeStorage(windowObject, storageName);
    if (!storage) throw new Error("browser_storage_boundary_unavailable");
    const proxy = createStorageProxy(storage);
    try {
      Object.defineProperty(windowObject, storageName, {
        configurable: false,
        enumerable: true,
        get: () => proxy,
      });
    } catch {
      throw new Error("browser_storage_boundary_unavailable");
    }
  }

  Object.defineProperty(windowObject, BOUNDARY_MARKER, {
    configurable: false,
    enumerable: false,
    writable: false,
    value: true,
  });
  return true;
}

export function installBrowserPrivacyBoundary(windowObject = globalThis.window) {
  const legacyValuesRemoved = purgeLegacyBrowserState(windowObject);
  if (!installStorageBoundary(windowObject)) {
    return Object.freeze({
      history_persistence: false,
      session_persistence: false,
      legacy_values_removed: legacyValuesRemoved,
      storage_proxy_enforced: true,
      source_enforcement_required: true,
    });
  }
  return Object.freeze({
    history_persistence: false,
    session_persistence: false,
    legacy_values_removed: legacyValuesRemoved,
    storage_proxy_enforced: true,
    source_enforcement_required: true,
  });
}
